import { useDeck } from "../../../lib/deck-context";
import { Card, Eyebrow, Note } from "../../deck/primitives";
import { CENTERS, SRC } from "./data";
import { HBars, Src, Stat, Tag } from "./ui";

const Level = ({ title, tag, rows, src, note, testid }) => (
  <Card data-testid={testid} className="p-7 h-full">
    <div className="flex items-center justify-between gap-3"><div className="font-display text-2xl text-ink">{title}</div><Tag t={tag} /></div>
    <div className="mt-5 divide-y divide-line/70">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 py-2.5 text-sm"><span className="text-steel">{k}</span><span className="text-ink tabular-nums text-right">{v}</span></div>
      ))}
    </div>
    {note && <p className="mt-4 text-sm text-char/80">{note}</p>}
    <Src s={src} />
  </Card>
);

export const TabOverview = () => {
  const { inputs } = useDeck();
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-5">
        <Level testid="mk-level-murrieta" title="Murrieta" tag="local" src={SRC.murrieta} note="Murrieta 2–4 Star Retail Submarket Snapshot." rows={[
          ["Vacancy", "~4.6%"], ["Occupancy", "~95.4%"], ["Average asking rent", "~$30.62/SF/yr"], ["12-month leasing activity", "~521,178 SF"], ["Average months on market", "~11.3 months"],
        ]} />
        <Level testid="mk-level-south-riverside" title="South Riverside" tag="local" src={SRC.sr}
          note="South Riverside has generally operated as one of the tighter retail areas within the Inland Empire. No current exact category statistic is shown where one has not been verified."
          rows={[["Trade area", "Murrieta · Temecula · Menifee · Wildomar · Lake Elsinore + surrounding"], ["Retail availability / vacancy", "≈ 5% or below in stronger portions"]]} />
        <Level testid="mk-level-inland-empire" title="Inland Empire" tag="regional" src={SRC.ie} rows={[
          ["Q3 2026 vacancy", "6.0%"], ["Occupancy", "94.0%"], ["Asking rent", "$1.77/SF/mo (~$21.24/SF/yr)"], ["Q3 net absorption", "+402,499 SF"],
          ["2026 YTD net absorption", "+1,245,186 SF"], ["Under construction", "~1.10M SF"], ["Average retail cap rate", "~7.0%"],
        ]} />
      </div>
      <Card className="p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
          <Eyebrow>Occupancy Benchmark</Eyebrow>
          <span className="text-sm text-arq">Our occupancy assumption is below current local and regional market occupancy.</span>
        </div>
        <HBars testid="mk-occupancy-chart" max={100} fmt={(v) => `${v.toFixed(1)}%`} rows={[
          { label: "Murrieta Retail (2–4 Star)", value: 95.4, dark: true },
          { label: "Inland Empire Retail", value: 94.0 },
          { label: "Murrieta Family Commons Underwriting", value: inputs.occ, hl: true },
        ]} />
        <Note className="mt-7">A conservative stabilization benchmark relative to current market conditions — not proof the project will reach {inputs.occ}%. Local figure is Murrieta-specific; regional figure is Inland Empire-wide.</Note>
      </Card>
    </div>
  );
};

export const TabRetail = () => (
  <div className="space-y-6">
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
      <Stat testid="mk-retail-asking" label="Murrieta 2–4 Star asking rent" value="~$30.62" sub="/SF/yr · asking, not executed" tag="avg" src={SRC.murrieta} type="Asking rent" />
      <Stat testid="mk-retail-overall" label="Murrieta market overall asking rent" value="~$26.84" sub="/SF/yr · asking" tag="avg" src={SRC.murrieta} type="Asking rent" />
      <Stat testid="mk-retail-occ" label="Local occupancy" value="~95.4%" sub="Murrieta 2–4 Star retail" tag="market" src={SRC.murrieta} type="Occupancy" />
      <Stat testid="mk-retail-target" label="Project retail underwriting target" value="94–95%" sub="Retail suites only — not applied to the whole project" tag="uw" accent src={SRC.arq} type="Underwriting" />
    </div>
    <div>
      <Eyebrow className="mb-2">Local Center Examples</Eyebrow>
      <p className="mb-5 text-sm text-steel">Examples of local performance. Not direct rent comps unless suite type and quality are comparable — Downtown Marketplace is the nearest and most relevant.</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {CENTERS.map((c) => (
          <Card key={c.id} data-testid={`mk-center-${c.id}`} className={`p-7 ${c.id === "dm" ? "ring-1 ring-arq/50" : ""}`}>
            <div className="flex flex-col items-start gap-2"><Tag t={c.quality} label={c.qLabel} /><div className="font-display text-xl text-ink">{c.name}</div></div>
            <div className="text-xs text-steel mt-1">{c.address}</div>
            <div className="mt-4 flex items-end gap-6">
              <div><div className="text-xs text-steel">{c.rentType} rent</div><div className={`font-display text-3xl tabular-nums ${c.id === "dm" ? "text-arq" : "text-ink"}`}>{c.rentLo === c.rentHi ? `$${c.rentLo}` : `$${c.rentLo}–$${c.rentHi}`}<span className="text-sm text-steel">/SF/yr</span></div></div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
              {[["Center type", c.type], ["Total SF", c.totalSf], ["Occupancy", c.occ], ["Available suites", c.suites], ["Asking / est. rent", c.rentText], ["Tenant mix", c.tenants], ["Parking", c.parking], ["Major anchors", c.anchors], ["Distance from subject", c.distance]].map(([k, v]) => (
                <div key={k} className="flex flex-col"><span className="text-steel">{k}</span><span className="text-char">{v}</span></div>
              ))}
            </div>
            <Src s={{ source: c.source, date: "2026", geo: "Murrieta", updated: "Oct 2026" }} type={`${c.rentType} rent`} />
          </Card>
        ))}
      </div>
    </div>
  </div>
);
